import { useMemo } from "react";
import { encode } from "uqr";

/**
 * QR code as inline SVG. Always dark modules on a light tile with a 4-module quiet zone, in both
 * themes: scanners expect that polarity, so it deliberately ignores the colour scheme. Reference
 * palette tokens only. `label` is the accessible name (the value itself is a secret).
 */
export function QrCode({ value, label, size = 176 }: { value: string; label: string; size?: number }) {
  const { path, dim } = useMemo(() => {
    const { data, size: n } = encode(value, { ecc: "M", border: 0 });
    const border = 4;
    let d = "";
    for (let y = 0; y < n; y++) {
      let x = 0;
      while (x < n) {
        if (!data[y]?.[x]) {
          x++;
          continue;
        }
        const start = x;
        while (x < n && data[y]?.[x]) x++;
        d += `M${start + border} ${y + border}h${x - start}v1h-${x - start}z`;
      }
    }
    return { path: d, dim: n + border * 2 };
  }, [value]);

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${dim} ${dim}`}
      width={size}
      height={size}
      shapeRendering="crispEdges"
      className="shrink-0 rounded-control border border-line"
    >
      <rect width={dim} height={dim} style={{ fill: "var(--gray-0)" }} />
      <path d={path} style={{ fill: "var(--gray-1000)" }} />
    </svg>
  );
}
